import { useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

export function SettingsForm({ settings, isSuper, onSaved }) {
  const [f, setF] = useState({
    membership_price_usd: settings.membership_price_usd,
    membership_price_ngn: settings.membership_price_ngn,
    international_price_usd: settings.international_price_usd,
    international_payment_price_ngn: settings.international_payment_price_ngn,
    platform_pct: settings.platform_pct,
    partner_max_pct: settings.partner_max_pct,
    withdrawal_threshold_usd: settings.withdrawal_threshold_usd,
    withdrawal_threshold_ngn: settings.withdrawal_threshold_ngn,
    partner_default_capacity: settings.partner_default_capacity,
    verify: settings.perf_weights.verify, plan: settings.perf_weights.plan,
    followup: settings.perf_weights.followup, support: settings.perf_weights.support,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });
  const weightSum = Number(f.verify) + Number(f.plan) + Number(f.followup) + Number(f.support);
  const allocSum = Number(f.platform_pct) + Number(f.partner_max_pct);

  const save = async () => {
    if (weightSum !== 100) { toast.error("Performance weights must total 100."); return; }
    if (allocSum !== 100) { toast.error("Platform + Partner allocation must total 100%."); return; }
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        membership_price_usd: Number(f.membership_price_usd), membership_price_ngn: Number(f.membership_price_ngn),
        international_price_usd: Number(f.international_price_usd), international_payment_price_ngn: Number(f.international_payment_price_ngn),
        platform_pct: Number(f.platform_pct), partner_max_pct: Number(f.partner_max_pct),
        withdrawal_threshold_usd: Number(f.withdrawal_threshold_usd), withdrawal_threshold_ngn: Number(f.withdrawal_threshold_ngn),
        partner_default_capacity: Number(f.partner_default_capacity),
        perf_weights: { verify: Number(f.verify), plan: Number(f.plan), followup: Number(f.followup), support: Number(f.support) },
      });
      toast.success("Settings saved.");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const Field = ({ k, label, suffix }) => (
    <div className="space-y-2">
      <Label className="text-zinc-300">{label}</Label>
      <div className="relative">
        <Input type="number" value={f[k]} onChange={(e) => set(k, e.target.value)} disabled={!isSuper} className="h-11 bg-zinc-900 border-zinc-800" data-testid={`setting-${k}`} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 max-w-3xl">
      {!isSuper && <p className="mb-4 text-sm text-amber-400 flex items-center gap-2"><Lock className="w-4 h-4" /> Only a Super Admin can change these values.</p>}
      <p className="mb-3 text-xs font-bold uppercase tracking-widest text-zinc-500">Membership Pricing</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field k="membership_price_ngn" label="Nigeria Membership (display + charge)" suffix="NGN/mo" />
        <Field k="international_price_usd" label="International Membership (display only)" suffix="USD/mo" />
        <Field k="international_payment_price_ngn" label="International Charge in NGN (Paystack)" suffix="NGN/mo" />
        <Field k="membership_price_usd" label="International USD Charge (future, when USD settlement on)" suffix="USD/mo" />
      </div>
      <p className="mt-6 text-xs font-bold uppercase tracking-widest text-zinc-500">Withdrawal Thresholds</p>
      <div className="mt-3 grid sm:grid-cols-2 gap-5">
        <Field k="withdrawal_threshold_usd" label="USD Withdrawal Threshold" suffix="USD" />
        <Field k="withdrawal_threshold_ngn" label="NGN Withdrawal Threshold" suffix="NGN" />
      </div>
      <p className="mt-6 text-xs font-bold uppercase tracking-widest text-zinc-500">Allocation (must total 100%) — currently {allocSum}</p>
      <div className="mt-3 grid sm:grid-cols-3 gap-5">
        <Field k="platform_pct" label="Platform Allocation" suffix="%" />
        <Field k="partner_max_pct" label="Partner Max Allocation" suffix="%" />
        <Field k="partner_default_capacity" label="Partner Default Capacity" suffix="members" />
      </div>
      <p className="mt-6 text-xs font-bold uppercase tracking-widest text-zinc-500">Performance Weights (must total 100) — currently {weightSum}</p>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Field k="verify" label="Verify" suffix="%" />
        <Field k="plan" label="Plan" suffix="%" />
        <Field k="followup" label="Follow-up" suffix="%" />
        <Field k="support" label="Support" suffix="%" />
      </div>
      {isSuper && (
        <Button onClick={save} disabled={saving} className="mt-6 bg-[#E53E3E] hover:bg-[#C53030] rounded-full font-semibold h-11 px-6" data-testid="save-settings-button">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Settings</>}
        </Button>
      )}
    </div>
  );
}
